import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import Slider from '../ui/Slider';
import SliderValue from '../ui/SliderValue';
import { campaignVariantService } from '../../services/campaignVariants';

type LoanAmountFormProps = {
  formData: FormData;
  onSubmit: (data: Partial<FormData>) => void;
};

export default function LoanAmountForm({ formData, onSubmit }: LoanAmountFormProps) {
  const variant = campaignVariantService.getCurrentVariant();
  const defaultAmount = formData.loanAmount || variant.defaultLoanAmount;
  const [amount, setAmount] = useState(defaultAmount);
  const minAmount = 1000;
  const maxAmount = 100000;
  const step = 500;
  
  const handleSliderChange = (value: number) => {
    setAmount(value);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleSubmit = () => {
    onSubmit({ loanAmount: amount });
  };

  const handleIncrement = () => {
    const newValue = Math.min(amount + step, maxAmount);
    handleSliderChange(newValue);
  };

  const handleDecrement = () => {
    const newValue = Math.max(amount - step, minAmount);
    handleSliderChange(newValue);
  };

  return (
    <div id="loan-amount-form" className="space-y-8">
      <div className="text-center">
        <Title 
          id="loan-amount-title"
          name="loan-amount-title"
          as="h1" 
          className="mb-2"
        >
          Loan Amount
        </Title>
        <Description 
          id="loan-amount-description"
          name="loan-amount-description"
          size="lg"
        >
          {variant.offerAmount ? 
            `You're eligible for ${formatCurrency(variant.offerAmount)}. You can adjust if needed.` :
            'How much would you like to borrow?'
          }
        </Description>
      </div>
      
      {variant.offerAmount && variant.offerAmount !== amount && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <p className="text-sm text-blue-700">
            <span className="font-medium">Original offer:</span> {formatCurrency(variant.offerAmount)}
          </p>
        </div>
      )}

      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <button
            id="loan-amount-decrease"
            name="loan-amount-decrease"
            type="button"
            onClick={handleDecrement}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            aria-label="Decrease amount"
          >
            <Minus className="w-5 h-5 text-gray-600" />
          </button>
          
          <SliderValue
            id="loan-amount-value"
            name="loan-amount-value"
            value={amount}
            min={minAmount}
            max={maxAmount}
            formatValue={formatCurrency}
          />
          
          <button
            id="loan-amount-increase"
            name="loan-amount-increase"
            type="button"
            onClick={handleIncrement}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            aria-label="Increase amount"
          >
            <Plus className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <Slider
          id="loan-amount-slider"
          name="loan-amount-slider"
          value={amount}
          min={minAmount}
          max={maxAmount}
          step={step}
          onChange={handleSliderChange}
          formatValue={formatCurrency}
          showLimits
        />

        <Button
          id="loan-amount-submit"
          name="loan-amount-submit"
          onClick={handleSubmit}
          fullWidth
          size="lg"
        >
          Continue
        </Button>
      </div>
    </div>
  );
}